import { getInvitationById } from "@/api/invitations";
import { formatDate } from "@/utils";
import { Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
    InvitationStatusDisplay,
    InvitationStatusValue,
} from "./InvitationStatusDisplay";
import { useResendInvitation } from "./hooks/useResendInvitation";

interface IPrefetchedInvitation {
    acceptedAt: string | null;
    lastSentAt: string;
}

interface IProps {
    invitationId: number | null | undefined;
    createdAt: string;
    /**
     * When given (the admin users table, where the row already carries
     * these two fields from the list endpoint), the fetch by id is
     * skipped entirely — avoids one network request per visible row.
     * When omitted (the user details page), fetches via getInvitationById.
     */
    prefetched?: IPrefetchedInvitation;
    /**
     * Called in addition to the default cache invalidation after a
     * successful resend. The details page doesn't need this (its own
     * `["invitation", id]` query is already invalidated by default); the
     * table passes one to also invalidate `["admin-users"]`.
     */
    onResendSuccess?: () => void;
    /**
     * Whether to show the send icon next to "Invitation sent...". Defaults
     * to true; the admin users table passes false to keep rows compact.
     */
    showIcon?: boolean;
}

/**
 * Shared "what's this user's registration/invitation status" display,
 * used by both `UserInformation` (details page) and the admin users
 * table's Invitation Status column — the one place this decision and its
 * copy live, so the two stay an exact copy of each other.
 */
export const UserRegistrationStatus = ({
    invitationId,
    createdAt,
    prefetched,
    onResendSuccess,
    showIcon = true,
}: IProps) => {
    const shouldFetch = !!invitationId && !prefetched;
    const { data: fetchedInvitation, isPending } = useQuery({
        queryKey: ["invitation", invitationId],
        queryFn: () => getInvitationById(invitationId as number),
        enabled: shouldFetch,
    });

    const { mutate: resend, isPending: isResending } = useResendInvitation(
        invitationId ?? 0,
        { onSuccess: onResendSuccess }
    );

    if (!invitationId) {
        return <Typography variant="body2">{formatDate(createdAt)}</Typography>;
    }

    const status: InvitationStatusValue = prefetched
        ? prefetched.acceptedAt
            ? { kind: "accepted", acceptedAt: prefetched.acceptedAt }
            : { kind: "pending", sentAt: prefetched.lastSentAt }
        : isPending
          ? { kind: "loading" }
          : !fetchedInvitation
            ? { kind: "missing" }
            : fetchedInvitation.accepted_at
              ? { kind: "accepted", acceptedAt: fetchedInvitation.accepted_at }
              : { kind: "pending", sentAt: fetchedInvitation.updated_at };

    return (
        <InvitationStatusDisplay
            status={status}
            onResend={() => resend()}
            isResending={isResending}
            showIcon={showIcon}
        />
    );
};
